import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import {
	Button,
	CopyButton,
	Notification,
	Table,
	useNotification,
} from "../../components/FormComponents";
import { TEMPLATE_CATEGORIES } from "../../constants/themes";

const CATEGORY_COLORS = {
	full: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
	hero: "bg-purple-500/10 text-purple-400 border-purple-500/20",
	products: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
	content: "bg-blue-500/10 text-blue-400 border-blue-500/20",
	footer: "bg-amber-500/10 text-amber-400 border-amber-500/20",
};

export default function Templates() {
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [categoryFilter, setCategoryFilter] = useState("all");
	const [search, setSearch] = useState("");
	const { notification, showNotification } = useNotification();
	const navigate = useNavigate();

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			const res = await client.get("/templates");
			setTemplates(Array.isArray(res.data) ? res.data : res.data.data || []);
		} catch (error) {
			console.error("Error fetching templates:", error);
			showNotification("Erro ao carregar templates", "error");
		} finally {
			setLoading(false);
		}
	}, [showNotification]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleDelete = async (id) => {
		if (window.confirm("Tem certeza que deseja deletar este template?")) {
			try {
				await client.delete(`/templates/${id}`);
				showNotification("Template deletado com sucesso!");
				fetchData();
			} catch (error) {
				console.error("Error deleting template:", error);
				showNotification("Erro ao deletar template", "error");
			}
		}
	};

	const filteredTemplates = templates.filter((t) => {
		const matchesCategory =
			categoryFilter === "all" || (t.category || "full") === categoryFilter;
		const matchesSearch =
			!search ||
			t.name.toLowerCase().includes(search.toLowerCase()) ||
			(t.description || "").toLowerCase().includes(search.toLowerCase());
		return matchesCategory && matchesSearch;
	});

	const categoryTabs = [
		{ value: "all", label: "Todos", count: templates.length },
		...TEMPLATE_CATEGORIES.map((c) => ({
			value: c.value,
			label: c.label,
			count: templates.filter((t) => (t.category || "full") === c.value).length,
		})),
	].filter((tab) => tab.value === "all" || tab.count > 0);

	const columns = [
		{
			key: "name",
			label: "Nome",
			render: (val) => <span className="font-medium text-white">{val}</span>,
		},
		{
			key: "category",
			label: "Categoria",
			render: (val) => {
				const cat = TEMPLATE_CATEGORIES.find(
					(c) => c.value === (val || "full"),
				);
				const colorClass =
					CATEGORY_COLORS[val || "full"] || CATEGORY_COLORS.full;
				return (
					<span
						className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${colorClass}`}
					>
						{cat?.label || val || "Completo"}
					</span>
				);
			},
		},
		{
			key: "company_name",
			label: "Empresa",
			render: (val) => <span className="text-slate-400">{val || "Todas"}</span>,
		},
		{
			key: "description",
			label: "Descricao",
			render: (val) => (
				<span className="text-slate-400 truncate max-w-[200px] block">
					{val || "-"}
				</span>
			),
		},
	];

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			<div className="flex items-center justify-between">
				<h1 className="text-2xl font-bold text-white">Templates de Proposta</h1>
				<Button onClick={() => navigate("/admin/templates/new")}>
					<Plus className="w-4 h-4 inline mr-2" />
					Novo Template
				</Button>
			</div>

			{/* Category tabs */}
			<div className="flex items-center gap-1 overflow-x-auto pb-1">
				{categoryTabs.map((tab) => (
					<button
						key={tab.value}
						type="button"
						onClick={() => setCategoryFilter(tab.value)}
						className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
							categoryFilter === tab.value
								? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
								: "text-slate-400 hover:text-white hover:bg-slate-800 border border-transparent"
						}`}
					>
						{tab.label}
						<span
							className={`ml-1.5 text-[10px] ${categoryFilter === tab.value ? "text-cyan-400/60" : "text-slate-600"}`}
						>
							{tab.count}
						</span>
					</button>
				))}
			</div>

			{/* Search */}
			<div className="relative">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
				<input
					type="text"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Buscar templates..."
					className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-white/10 rounded-lg text-slate-300 placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 transition-all"
				/>
			</div>

			<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6">
				<Table
					columns={columns}
					data={filteredTemplates}
					loading={loading}
					onEdit={(template) =>
						navigate(`/admin/templates/new?edit=${template.id}`)
					}
					onDelete={handleDelete}
					actions={(row) =>
						row.template_code && (
							<CopyButton
								text={row.template_code}
								label="Copiar Codigo"
								size="xs"
							/>
						)
					}
				/>
			</div>
		</div>
	);
}
